import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import BusinessCard from '../components/BusinessCard';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [category, setCategory] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      const [catRes, bizRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/businesses?category_id=${categoryId}`)
      ]);
      
      const foundCategory = catRes.data.find(c => c.id === categoryId);
      setCategory(foundCategory);
      setBusinesses(bizRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryName = category ? (language === 'ta' ? category.name_ta : category.name_en) : '';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="category-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-12">
        <div className="container mx-auto px-4 md:px-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('home')}
          </Button>
          
          {category && (
            <div className="flex items-center gap-6">
              <img
                src={category.image_url}
                alt={categoryName}
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">
                  {categoryName}
                </h1>
                <p className="text-blue-100 mt-1">
                  {businesses.length} {language === 'ta' ? 'பட்டியல்கள்' : 'listings'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Businesses Grid */}
      <div className="container mx-auto px-4 md:px-6 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl h-72" />
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">{t('noResults')}</p>
            <Button
              onClick={() => navigate('/add-shop')}
              className="mt-4 bg-[#E85A24] hover:bg-[#d14918]"
            >
              {t('addYourShop')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((business) => (
              <BusinessCard key={business.id} business={business} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
